import { useEffect, useState, useMemo } from "react";
import Layout from "@/components/app/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Navigate } from "react-router-dom";
import { Building2, Users, Briefcase, UserRound, Phone, Film, Code2, Search, Loader2, Shield, Calendar } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import LiveInterviewsPanel from "@/components/app/LiveInterviewsPanel";
import SupportInbox from "@/components/app/SupportInbox";

/**
 * Workspace Explorer — Platform Owner-only single pane of every provisioned
 * tenant with activity counts and the owning admin. Mounted at
 * /app/workspaces and hidden from every non-platform-owner role.
 */
export default function WorkspacesPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ workspaces: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (user?.role !== "platform_owner") return;
    (async () => {
      try {
        const { data } = await api.get("/admin/workspaces");
        setData(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load workspaces");
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return data.workspaces;
    return data.workspaces.filter((w) =>
      [w.name, w.admin?.email, w.admin?.name, w.id].filter(Boolean).some((v) => v.toLowerCase().includes(needle))
    );
  }, [data.workspaces, q]);

  // Hard-gate: non-platform-owners shouldn't be able to open this URL directly.
  if (user && user.role !== "platform_owner") return <Navigate to="/app" replace />;

  const totals = data.totals || {};

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-6" data-testid="workspaces-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-display font-semibold text-slate-900 flex items-center gap-2">
              <Shield className="w-7 h-7 text-indigo-600" /> Workspace Explorer
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Every provisioned tenant on RecruitSift, with live activity counts.
            </p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by workspace or admin…"
              className="pl-9"
              data-testid="workspaces-search"
            />
          </div>
        </div>
        <LiveInterviewsPanel />
        <SupportInbox />

        {/* Totals bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="workspaces-totals">
          <TotalTile icon={Building2} label="Tenants" value={totals.tenants ?? 0} />
          <TotalTile icon={Users} label="Users" value={totals.users ?? 0} />
          <TotalTile icon={Briefcase} label="Jobs" value={totals.jobs ?? 0} />
          <TotalTile icon={UserRound} label="Candidates" value={totals.candidates ?? 0} />
          <TotalTile icon={Phone} label="Phone" value={totals.phone ?? 0} />
          <TotalTile icon={Film} label="Video" value={totals.video ?? 0} />
          <TotalTile icon={Code2} label="Coding" value={totals.coding ?? 0} />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading workspaces…
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500" data-testid="workspaces-empty">
            {q ? "No workspaces match that search." : "No workspaces yet."}
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="hidden md:grid grid-cols-[2fr_2fr_1fr_repeat(6,minmax(0,1fr))] gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-medium">
              <div>Workspace</div>
              <div>Admin</div>
              <div>Created</div>
              <div className="text-center">Users</div>
              <div className="text-center">Jobs</div>
              <div className="text-center">Cands</div>
              <div className="text-center">Phone</div>
              <div className="text-center">Video</div>
              <div className="text-center">Coding</div>
            </div>
            {rows.map((w) => (
              <div
                key={w.id}
                data-testid={`workspace-row-${w.id}`}
                className="grid md:grid-cols-[2fr_2fr_1fr_repeat(6,minmax(0,1fr))] gap-3 px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 text-sm"
              >
                <div className="min-w-0 flex items-center gap-3">
                  {w.logo_data_url ? (
                    <img src={w.logo_data_url} alt="" className="w-8 h-8 rounded object-contain bg-white border border-slate-200 flex-shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-4 h-4" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{w.name}</div>
                    <div className="text-[11px] text-slate-400 font-mono truncate">{w.id}</div>
                  </div>
                </div>
                <div className="min-w-0">
                  {w.admin ? (
                    <>
                      <div className="text-slate-900 truncate">{w.admin.name || "—"}</div>
                      <div className="text-[11px] text-slate-500 truncate">{w.admin.email}</div>
                    </>
                  ) : (
                    <div className="text-slate-400 text-xs italic">no admin yet</div>
                  )}
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {w.created_at ? new Date(w.created_at).toLocaleDateString() : "—"}
                </div>
                <CountCell value={w.counts.users} />
                <CountCell value={w.counts.jobs} />
                <CountCell value={w.counts.candidates} />
                <CountCell value={w.counts.phone} />
                <CountCell value={w.counts.video} />
                <CountCell value={w.counts.coding} />
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function TotalTile({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex items-center gap-2 text-slate-500 text-[11px] uppercase tracking-wider">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-2xl font-display font-semibold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function CountCell({ value }) {
  return (
    <div className="text-center">
      <span className={value > 0 ? "text-slate-900 font-medium" : "text-slate-300"}>{value}</span>
    </div>
  );
}
